import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, LinearProgress, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography
} from '@mui/material';
import { apiError } from '../format';
import { isCancelled } from '../api';
import { rulesApi } from './rulesApi';
import { BORDER, DARK, FIELD_SX, SELECT_MENU_PROPS, KIND_LABELS, KIND_ORDER, MODE_LABELS, MUTED, PRIMARY, fmtDbDateTime } from './rulesConstants';
import { EmptyState, HEAD_ROW_SX, ModeChip, Panel, ScopeCell, TABLE_SX } from './RuleBits';
import { Paged, RuleEvent, RulesCatalog } from './rulesTypes';

interface Props { catalog: RulesCatalog; refreshKey: number }

const EMPTY_PAGE: Paged = { page: 1, limit: 25, total: 0, totalPages: 1 };

/** Вкладка «Журнал срабатываний»: когда и какое правило сработало (блокировка или наблюдение) */
const RuleEventsTab: React.FC<Props> = ({ catalog, refreshKey }) => {
  const [rows, setRows] = useState<RuleEvent[]>([]);
  const [pg, setPg] = useState<Paged>(EMPTY_PAGE);
  const [ruleCode, setRuleCode] = useState('');
  const [docKind, setDocKind] = useState('');
  const [mode, setMode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    setLoading(true);
    rulesApi.events({ rule_code: ruleCode, doc_kind: docKind, mode, dateFrom, dateTo, page, limit }, signal)
      .then(res => { setRows(res.rows); setPg(res.pagination); setError(null); })
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить журнал')); })
      .finally(() => { if (!signal.aborted) setLoading(false); });
  }, [ruleCode, docKind, mode, dateFrom, dateTo, page, limit]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);

  const filter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Правило" value={ruleCode} onChange={e => filter(setRuleCode)(e.target.value)} sx={{ minWidth: 240, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Все правила</MenuItem>
          {catalog.rules.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Вид документа" value={docKind} onChange={e => filter(setDocKind)(e.target.value)} sx={{ minWidth: 190, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Все виды</MenuItem>
          {KIND_ORDER.map(k => <MenuItem key={k} value={k}>{KIND_LABELS[k]}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Результат" value={mode} onChange={e => filter(setMode)(e.target.value)} sx={{ minWidth: 150, ...FIELD_SX }} SelectProps={{ MenuProps: SELECT_MENU_PROPS }}>
          <MenuItem value="">Любой</MenuItem>
          <MenuItem value="enforce">Заблокировано</MenuItem>
          <MenuItem value="observe">{MODE_LABELS.observe}</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="С" value={dateFrom} onChange={e => filter(setDateFrom)(e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
        <TextField size="small" type="date" label="По" value={dateTo} onChange={e => filter(setDateTo)(e.target.value)} InputLabelProps={{ shrink: true }} sx={FIELD_SX} />
        {loading && <CircularProgress size={18} sx={{ color: PRIMARY }} />}
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 2.5 }}>{error}</Alert>}

      <Panel sx={{ position: 'relative' }}>
        {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />}
        {!loading && rows.length === 0 ? (
          <EmptyState
            title="Срабатываний пока нет"
            text="Журнал заполняется при создании документов с проверкой правил; проверки на вкладке «Проверка» в него не пишутся."
          />
        ) : (
          <TableContainer>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow sx={HEAD_ROW_SX}>
                  <TableCell>Время</TableCell>
                  <TableCell>Правило</TableCell>
                  <TableCell>Вид документа</TableCell>
                  <TableCell>Аптека / направление</TableCell>
                  <TableCell>Результат</TableCell>
                  <TableCell align="right">Лимит / факт</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Сообщение</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(e => {
                  const def = catalog.rules.find(r => r.code === e.rule_code);
                  return (
                    <TableRow key={e.id} sx={{ '& td': { py: 1.25 } }}>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: MUTED }}>{fmtDbDateTime(e.created_at)}</TableCell>
                      <TableCell sx={{ color: DARK, fontWeight: 600 }}>{def?.name || e.rule_code}</TableCell>
                      <TableCell sx={{ color: MUTED }}>{KIND_LABELS[e.doc_kind]}</TableCell>
                      <TableCell><ScopeCell scopeType={def?.scopeType || 'pharmacy'} from={e.from_name} to={e.to_name} /></TableCell>
                      <TableCell><ModeChip mode={e.mode} label={e.mode === 'enforce' ? 'Заблокировано' : 'Наблюдение'} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: DARK, fontWeight: 600 }}>
                        <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>{e.limit_value ?? '-'} / </Box>{e.actual_value ?? '-'}
                      </TableCell>
                      <TableCell>{e.username || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography sx={{ fontSize: 13, color: MUTED }} title={e.message || ''} noWrap>{e.message || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div" count={pg.total} page={pg.page - 1} rowsPerPage={limit} rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setPage(p + 1)} onRowsPerPageChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
          labelRowsPerPage="Строк:" labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ borderTop: `1px solid ${BORDER}`, color: MUTED, '& .MuiTablePagination-toolbar': { minHeight: 48 } }}
        />
      </Panel>
    </Box>
  );
};

export default RuleEventsTab;
